import { logger } from 'utils/logging'

/** Marks the footer's Next CTA (its click wrapper) for the screen-entry measurement (ISSUE-4023). */
export const PURCHASE_FLOW_NEXT_CTA_ATTRIBUTE = 'data-purchase-flow-next-cta'
/** Marks the purchase flow's scrolling step column for the screen-entry measurement (ISSUE-4023). */
export const PURCHASE_FLOW_STEP_COLUMN_ATTRIBUTE = 'data-purchase-flow-step-column'
/**
 * How long the CTA visibility may take. An observer only reports after the browser paints, and the Propstack embed
 * mounts hidden (ISSUE-2700) or sits in a background tab, so a silent observer must not hold the funnel event.
 */
export const SCREEN_ENTRY_MEASUREMENT_TIMEOUT_MS = 1_000
/** Share of the CTA that must be visible to count as in the viewport: a host page can clip the iframe. */
const CTA_VISIBLE_RATIO = 0.95

/** What the broker could see when a screen rendered; null when the value could not be measured. */
export interface ScreenEntryLayout {
  /** The footer's Next button is inside the visible area. Expected true throughout: the footer is pinned. */
  cta_in_viewport: boolean | null
  /** The step content is taller than its scrolling column, so part of it sits below the fold. */
  step_content_overflows: boolean | null
}

/** The layout of a screen that was left before it could be measured. */
export const UNMEASURED_SCREEN_ENTRY_LAYOUT: ScreenEntryLayout = { cta_in_viewport: null, step_content_overflows: null }

let missingMarkerWarned = false

/**
 * One-shot visibility of the CTA, or null when the observer stayed silent past the timeout. The observer also
 * accounts for the host page clipping the embed iframe; without one (old browsers, jsdom) the iframe-local
 * geometry decides.
 */
const isCtaInViewport = (cta: Element): Promise<boolean | null> => {
  if (typeof IntersectionObserver === 'undefined') {
    const rect = cta.getBoundingClientRect()
    return Promise.resolve(rect.height > 0 && rect.top >= 0 && rect.bottom <= window.innerHeight)
  }

  return new Promise((resolve) => {
    let timer: number | undefined
    const observer = new IntersectionObserver((entries) => {
      window.clearTimeout(timer)
      observer.disconnect()
      resolve(entries.some((entry) => entry.intersectionRatio >= CTA_VISIBLE_RATIO))
    }, { threshold: CTA_VISIBLE_RATIO })
    timer = window.setTimeout(() => {
      observer.disconnect()
      resolve(null)
    }, SCREEN_ENTRY_MEASUREMENT_TIMEOUT_MS)
    observer.observe(cta)
  })
}

/**
 * Measures the screen-entry layout for `cta_in_viewport` and `step_content_overflows` on the enters-screen events
 * (ISSUE-4023), in `column` (the new flow passes the page). Resolves after the observer's first report, at the latest
 * after the timeout, and never rejects: the funnel event must go out. A missing marker is warned once per session.
 */
export const measureScreenEntryLayout = async (
  column: Element | null = document.querySelector(`[${PURCHASE_FLOW_STEP_COLUMN_ATTRIBUTE}]`)
): Promise<ScreenEntryLayout> => {
  const cta = document.querySelector(`[${PURCHASE_FLOW_NEXT_CTA_ATTRIBUTE}]`)

  if ((!cta || !column) && !missingMarkerWarned) {
    missingMarkerWarned = true
    logger.warn('Screen-entry measurement found no marker for the CTA or the step column', undefined, {
      context: { component: 'screenEntryLayout', action: 'screenEntryMarkerMissing', metadata: { hasCta: !!cta, hasColumn: !!column } },
    })
  }

  const stepContentOverflows = column ? column.scrollHeight > column.clientHeight : null

  try {
    return { cta_in_viewport: cta ? await isCtaInViewport(cta) : null, step_content_overflows: stepContentOverflows }
  } catch (error) {
    logger.warn('Screen-entry measurement failed', error, {
      context: { component: 'screenEntryLayout', action: 'screenEntryMeasurementFailed' },
    })
    return { cta_in_viewport: null, step_content_overflows: stepContentOverflows }
  }
}
